import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMedicalCertificate, useReferringDoctors, usePatients, useVisit, useClinicDoctors, useAppSettings } from "@/lib/queries";
import { useCreateMedicalCertificate, useUpdateMedicalCertificate, useSendCertificateWebhook, useAiPolish } from "@/lib/mutations";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Save, Send, Printer, Sparkles, WandSparkles } from "lucide-react";
import { DEFAULT_AI_CONTEXTS, getAiContext } from "@emr/shared/ai";
import { RECIPIENT_TYPES, type RecipientType } from "@emr/shared/constants";
import { createCertificatePdf } from "@/lib/pdf/certificatePdf";
import { savePdfToR2 } from "@/lib/r2";
import { printPdf } from "@/lib/print";
import { toast } from "sonner";
import { fmtDate } from "@emr/shared/india";

export function NewMedicalCertificate() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const visitIdParam = searchParams.get("visitId");

  const { data: existing, isLoading } = useMedicalCertificate(id);
  const { data: doctorsData } = useReferringDoctors();
  const { data: clinicDoctorsData } = useClinicDoctors();
  const { data: settings } = useAppSettings();
  const doctors: any[] = (doctorsData as any)?.rows ?? (Array.isArray(doctorsData) ? doctorsData : []) ?? [];
  const clinicDoctors: any[] = (clinicDoctorsData as any)?.rows ?? (Array.isArray(clinicDoctorsData) ? clinicDoctorsData : []) ?? [];

  const [form, setForm] = useState({
    patientId: "" as string,
    patientQuery: "",
    visitId: (visitIdParam ?? "") as string,
    createdByDoctorId: "" as string,
    recipientType: "whomsoever" as RecipientType,
    recipientName: "" as string,
    recipientSpecialization: "" as string,
    recipientClinic: "" as string,
    recipientAddress: "" as string,
    certificateBody: "" as string,
    aiContext: DEFAULT_AI_CONTEXTS.certificate as string,
  });
  const [updatedAt, setupdatedAt] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  const create = useCreateMedicalCertificate();
  const update = useUpdateMedicalCertificate(id ?? "");
  const sendWebhook = useSendCertificateWebhook();
  const polish = useAiPolish();

  const { data: patientsData } = usePatients(form.patientQuery, 1, 10);
  const patientOptions = patientsData?.rows ?? [];
  const { data: visitData } = useVisit(form.visitId || undefined);

  useEffect(() => {
    if (existing?.certificate) {
      const c = existing.certificate;
      setForm({
        patientId: c.patientId ?? "",
        patientQuery: "",
        visitId: c.visitId ?? "",
        createdByDoctorId: c.createdByDoctorId ?? "",
        recipientType: c.recipientType,
        recipientName: c.recipientName ?? "",
        recipientSpecialization: c.recipientSpecialization ?? "",
        recipientClinic: c.recipientClinic ?? "",
        recipientAddress: c.recipientAddress ?? "",
        certificateBody: c.certificateBody ?? "",
        aiContext: c.aiContext ?? getAiContext("certificate", settings?.aiPromptCertificate),
      });
      setupdatedAt(c.updatedAt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- existing is the only trigger; settings is only for the aiContext fallback
  }, [existing]);

  // Sync the AI system prompt from clinic settings when they load (for new certificates only,
  // not when editing an existing one — the saved aiContext is preserved above).
  useEffect(() => {
    if (!id && settings) {
      setForm((f) => ({ ...f, aiContext: getAiContext("certificate", settings.aiPromptCertificate) }));
    }
  }, [settings, id]);

  useEffect(() => {
    if (visitData?.visit) {
      const v = visitData.visit;
      setForm((f) => ({
        ...f,
        patientId: v.patientId,
        createdByDoctorId: v.doctorId ?? f.createdByDoctorId,
      }));
    }
  }, [visitData]);

  const webhookConfigured = settings?.certificateWebhookEnabled && settings?.certificateWebhookUrl;

  const doGenerateDraft = async () => {
    const userMessage = visitData?.note
      ? JSON.stringify({
          patient: visitData.visit.patientName,
          diagnosis: visitData.note.diagnosis,
          plan: visitData.note.plan,
        })
      : form.certificateBody || "Generate a generic medical certificate for rest and recovery.";
    try {
      const res = await polish.mutateAsync({ text: userMessage, aiContext: form.aiContext });
      setForm((f) => ({ ...f, certificateBody: res.text }));
      toast.success("Draft generated");
    } catch { /* handled */ }
  };

  const doPolish = async () => {
    if (!form.certificateBody.trim()) { toast.info("Nothing to polish."); return; }
    try {
      const res = await polish.mutateAsync({ text: form.certificateBody, aiContext: form.aiContext });
      setForm((f) => ({ ...f, certificateBody: res.text }));
      toast.success("Polished");
    } catch { /* handled */ }
  };

  const buildPdfData = () => {
    const cert = existing?.certificate;
    const patient = patientOptions.find((p: any) => p.id === form.patientId);
    // Salutation by referring doctor gender: look up referring doctor by name match (best-effort).
    const refDoc = doctors.find((d: any) => d.name === form.recipientName);
    return {
      recipientType: form.recipientType,
      patientName: cert?.patientName ?? (patient ? [patient.salutation, patient.name].filter(Boolean).join(" ") : null),
      clinicUhid: cert?.clinicUhid ?? patient?.clinicUhid ?? null,
      patientDob: cert?.patientDob ?? patient?.dob ?? null,
      patientGender: cert?.patientGender ?? patient?.gender ?? null,
      recipientName: form.recipientName || null,
      recipientSpecialization: form.recipientSpecialization || null,
      recipientClinic: form.recipientClinic || null,
      recipientAddress: form.recipientAddress || null,
      recipientGender: (refDoc?.gender as "Male" | "Female" | "Other") ?? null,
      certificateBody: form.certificateBody || null,
      signatoryName: clinicDoctors.find((d: any) => d.id === form.createdByDoctorId)?.name ?? null,
      signatoryQualifications: clinicDoctors.find((d: any) => d.id === form.createdByDoctorId)?.qualifications ?? null,
      signatoryRegNumber: clinicDoctors.find((d: any) => d.id === form.createdByDoctorId)?.regNumber ?? null,
      date: new Date().toISOString(),
    };
  };

  const save = async (opts?: { print?: boolean; webhook?: boolean }) => {
    if (form.recipientType === "specific" && !form.recipientName) { toast.error("Recipient name required for specific certificates."); return; }
    setBusy(true);
    try {
      const payload: Record<string, unknown> = {
        patientId: form.patientId || null,
        visitId: form.visitId || null,
        createdByDoctorId: form.createdByDoctorId || null,
        recipientType: form.recipientType,
        recipientName: form.recipientName || null,
        recipientSpecialization: form.recipientSpecialization || null,
        recipientClinic: form.recipientClinic || null,
        recipientAddress: form.recipientAddress || null,
        certificateBody: form.certificateBody || null,
        aiContext: form.aiContext || null,
      };
      let savedId = id;
      if (id) {
        payload.updatedAt = updatedAt;
        const res = await update.mutateAsync(payload);
        setupdatedAt((res as any)?.updatedAt);
      } else {
        const res = await create.mutateAsync(payload);
        savedId = (res as any)?.id;
        if (savedId) navigate(`/certificates/${savedId}`, { replace: true });
      }

      try {
        const doc = await createCertificatePdf(buildPdfData());
        if (opts?.print) {
          printPdf(doc);
        }
        if (savedId) {
          const patient = patientOptions.find((p: any) => p.id === form.patientId) ?? existing?.certificate as any;
          const fullName = patient?.name ? [patient?.salutation, patient?.name].filter(Boolean).join(" ") : (patient?.patientName ?? "Certificate");
          const fileName = `${fullName}_${fmtDate(new Date()).replace(/\//g, "-")}_${savedId.slice(0, 6)}`;
          const r2Key = await savePdfToR2(doc, "certificates", fileName, form.patientId, form.visitId || undefined);
          await update.mutateAsync({ savedToR2: true, r2Key: r2Key });
          toast.success("Saved + uploaded to R2");
        }
      } catch (e) {
        toast.error("PDF generation/R2 upload failed — certificate saved without PDF.");
      }

      if (opts?.webhook && savedId) {
        try {
          await sendWebhook.mutateAsync(savedId);
        } catch { /* handled */ }
      }
    } finally {
      setBusy(false);
    }
  };

  if (isLoading) return <div className="p-6"><Skeleton className="h-96" /></div>;

  return (
    <div className="mx-auto max-w-5xl p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/certificates")}><ArrowLeft size={18} /></Button>
          <h1 className="font-serif text-2xl font-bold text-maroon-700 dark:text-amber-500">{id ? "Edit Certificate" : "New Certificate"}</h1>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle className="font-serif text-lg">Details</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <Label>Recipient Type</Label>
            <Select value={form.recipientType} onValueChange={(val) => setForm({ ...form, recipientType: val as RecipientType })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {RECIPIENT_TYPES.map((t) => <SelectItem key={t} value={t}>{t === "whomsoever" ? "Whomsoever" : "Specific recipient"}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Patient (optional for whomsoever)</Label>
            <Input value={form.patientQuery} onChange={(e) => setForm({ ...form, patientQuery: e.target.value })} placeholder="Type name/phone/UHID to search…" />
            {patientOptions.length > 0 && (
              <div className="mt-1 max-h-40 overflow-auto rounded border border-cream-200 bg-cream-100">
                {patientOptions.map((p: any) => (
                  <button key={p.id} type="button" className="block w-full px-3 py-1 text-left text-sm hover:bg-cream-200" onClick={() => { setForm((f) => ({ ...f, patientId: p.id, patientQuery: [p.salutation, p.name].filter(Boolean).join(" ") })); }}>
                    {[p.salutation, p.name].filter(Boolean).join(" ")} · {p.clinicUhid} · {p.phone}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <Label>Signatory (clinic doctor)</Label>
            <Select value={form.createdByDoctorId} onValueChange={(v) => setForm({ ...form, createdByDoctorId: v })}>
              <SelectTrigger><SelectValue placeholder="Select clinic doctor" /></SelectTrigger>
              <SelectContent>
                {clinicDoctors.map((d: any) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Visit (optional)</Label>
            <Input value={form.visitId} onChange={(e) => setForm({ ...form, visitId: e.target.value })} placeholder="Visit UUID" />
          </div>
          {form.recipientType === "specific" && (
            <>
              <div><Label>Recipient Name *</Label><Input value={form.recipientName} onChange={(e) => setForm({ ...form, recipientName: e.target.value })} /></div>
              <div><Label>Specialization</Label><Input value={form.recipientSpecialization} onChange={(e) => setForm({ ...form, recipientSpecialization: e.target.value })} /></div>
              <div><Label>Clinic/Hospital</Label><Input value={form.recipientClinic} onChange={(e) => setForm({ ...form, recipientClinic: e.target.value })} /></div>
              <div><Label>Address</Label><Input value={form.recipientAddress} onChange={(e) => setForm({ ...form, recipientAddress: e.target.value })} /></div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="font-serif text-lg flex items-center gap-2"><Sparkles size={16} /> AI Context</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          <Textarea value={form.aiContext} onChange={(e) => setForm({ ...form, aiContext: e.target.value })} rows={3} />
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={doGenerateDraft} disabled={polish.isPending}><WandSparkles size={14} className="mr-1" /> Generate draft</Button>
            <Button variant="outline" size="sm" onClick={doPolish} disabled={polish.isPending || !form.certificateBody.trim()}><Sparkles size={14} className="mr-1" /> Polish</Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="font-serif text-lg">Certificate Body</CardTitle></CardHeader>
        <CardContent>
          <Textarea value={form.certificateBody} onChange={(e) => setForm({ ...form, certificateBody: e.target.value })} rows={10} placeholder="Write or generate the certificate body. Markdown supported." />
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-2">
        <Button onClick={() => save()} disabled={busy || (form.recipientType === "specific" && !form.recipientName)} className="bg-maroon-600 hover:bg-maroon-700 text-cream-50"><Save size={14} className="mr-1" /> Save</Button>
        <Button variant="outline" onClick={() => save({ print: true })} disabled={busy || (form.recipientType === "specific" && !form.recipientName)}><Printer size={14} className="mr-1" /> Save & Print</Button>
        <Button variant="outline" onClick={() => save({ webhook: true })} disabled={busy || !webhookConfigured} title={!webhookConfigured ? "No certificate webhook configured — enable it in Settings → Integrations" : ""}><Send size={14} className="mr-1" /> Save & Send Webhook</Button>
        <Button variant="ghost" onClick={() => navigate("/certificates")}>Cancel</Button>
      </div>
    </div>
  );
}
